import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, PencilSimple, TrashSimple, Star } from '@phosphor-icons/react';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import {
  microcycleTemplatesEndpoints,
  IMicrocycleTemplate,
  MicrocycleScope,
} from '../../endpoints/microcycleTemplates';
import { useSessionTaxonomy } from '../CoachSessionDetail/useSessionTaxonomy';
import { TemplateEditorModal } from './TemplateEditorModal';
import styles from './MicrocycleTemplates.module.scss';

/**
 * Lista template microciclo (sistema LEOPART + personali).
 * Spec v1.1.1 sez 15 righe 1270-1350.
 *
 * Filtri: scope (system/team/personal), sport (da team corrente).
 * Azioni: crea nuovo, duplica, modifica, elimina.
 */
const MicrocycleTemplatesPage: FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();
  const { sport } = useSessionTaxonomy(teamId);
  const [scopeFilter, setScopeFilter] = useState<MicrocycleScope | 'ALL'>(
    'ALL',
  );
  const [editing, setEditing] = useState<IMicrocycleTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['microcycleTemplates', scopeFilter, sport],
    queryFn: () =>
      microcycleTemplatesEndpoints
        .list({
          scope: scopeFilter === 'ALL' ? undefined : scopeFilter,
          sport: sport ?? undefined,
        })
        .then((r) => r.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      microcycleTemplatesEndpoints.delete(id).then((r) => r.data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['microcycleTemplates'] }),
  });

  const handleDelete = (t: IMicrocycleTemplate) => {
    const ok = window.confirm(`Eliminare il template "${t.name}"?`);
    if (ok) deleteMut.mutate(t._id);
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          <div className={styles.toolbar}>
            <Typhography type="h1" size="bold-20">
              Template Microciclo
            </Typhography>
            <div className={styles.toolbarRight}>
              <div className={styles.filterPill}>
                {(['ALL', 'SYSTEM', 'TEAM', 'PERSONAL'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`${styles.filterBtn} ${
                      scopeFilter === s ? styles.filterBtnActive : ''
                    }`}
                    onClick={() => setScopeFilter(s as any)}
                  >
                    {SCOPE_LABELS[s]}
                  </button>
                ))}
              </div>
              <CustomButton
                color="secondary"
                size="sm"
                onClick={() => setIsCreating(true)}
              >
                <Plus size={14} weight="bold" />
                Nuovo template
              </CustomButton>
            </div>
          </div>

          {isLoading && (
            <div className={styles.loading}>
              <LeopartLoader size="md" />
            </div>
          )}

          {!isLoading && templates.length === 0 && (
            <div className={styles.emptyState}>
              <span>Nessun template per questo filtro.</span>
            </div>
          )}

          {!isLoading && templates.length > 0 && (
            <div className={styles.grid}>
              {templates.map((t) => (
                <TemplateCard
                  key={t._id}
                  template={t}
                  onEdit={() => setEditing(t)}
                  onDelete={() => handleDelete(t)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {(isCreating || editing) && (
        <TemplateEditorModal
          template={editing}
          sport={sport ?? 'soccer'}
          teamId={teamId}
          onClose={() => {
            setEditing(null);
            setIsCreating(false);
          }}
          onSaved={() => {
            queryClient.invalidateQueries({
              queryKey: ['microcycleTemplates'],
            });
            setEditing(null);
            setIsCreating(false);
          }}
        />
      )}
    </div>
  );
};

const SCOPE_LABELS: Record<string, string> = {
  ALL: 'Tutti',
  SYSTEM: 'LEOPART',
  TEAM: 'Team',
  PERSONAL: 'Personali',
};

const TemplateCard: FC<{
  template: IMicrocycleTemplate;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ template, onEdit, onDelete }) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <div className={styles.cardTitle}>
        {template.isGlobalTemplate && (
          <Star size={14} color="#f5b350" weight="fill" />
        )}
        {template.name}
      </div>
      <span className={styles.scopeBadge}>{template.scope}</span>
    </div>
    {template.description && (
      <div className={styles.cardDesc}>{template.description}</div>
    )}
    <div className={styles.cardMeta}>
      <span className={styles.metaItem}>{template.sport}</span>
      {template.category && (
        <span className={styles.metaItem}>{template.category}</span>
      )}
      {template.mdPattern && (
        <span className={styles.metaItem}>{template.mdPattern}</span>
      )}
      <span className={styles.metaItem}>
        {template.structure.length} giorni
      </span>
      {template.usageCount != null && template.usageCount > 0 && (
        <span className={styles.metaItem}>
          Usato {template.usageCount} volte
        </span>
      )}
    </div>
    <div className={styles.cardActions}>
      {!template.isGlobalTemplate && (
        <button
          type="button"
          className={styles.actionBtn}
          onClick={onEdit}
          title="Modifica"
        >
          <PencilSimple size={13} />
        </button>
      )}
      {!template.isGlobalTemplate && (
        <button
          type="button"
          className={`${styles.actionBtn} ${styles.actionDanger}`}
          onClick={onDelete}
          title="Elimina"
        >
          <TrashSimple size={13} />
        </button>
      )}
    </div>
  </div>
);

export default MicrocycleTemplatesPage;
